import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { TableBody, TableHead, TableRow } from '@mui/material';
import { Box } from '@mui/material';
import { TableCell, Table } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ColumnDef,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';
import sort from 'src/assets/sort.svg';
import { Pagination } from 'src/components/pagination/pagination';

export const ListContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  minHeight: 0,
});

export const TableContainerWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
}));

export const TableWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  minHeight: 0,
  overflowX: 'auto',
  overflowY: 'hidden',
});

export const PaginationContainer = styled(Box)({
  paddingTop: '16px',
  display: 'flex',
  justifyContent: 'center',
  maxWidth: '2000px',
  flexShrink: 0,
});

export const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: theme.typography.subtitle2.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  backgroundColor: theme.palette.grey[100],
  borderBottom: `1px solid ${theme.palette.divider}`,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  padding: '16px',
}));

/** Structural cell only: no padding so content/link can fill the row with no gaps */
export const StyledBodyCell = styled(TableCell)(({ theme }) => ({
  padding: 0,
  verticalAlign: 'middle',
  overflow: 'hidden',
  maxWidth: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

/** Inner content wrapper: padding and typography live here (inside link when row is clickable) */
export const BodyCellInner = styled('div')<{ $isPrimary?: boolean }>(
  ({ theme, $isPrimary }) => ({
    padding: '16px',
    fontSize: theme.typography.subtitle2.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minHeight: '100%',
    boxSizing: 'border-box',
    ...($isPrimary && {
      color: theme.palette.primary.main,
    }),
  }),
);

/** Link that fills the entire cell so the whole row is clickable with no gaps */
export const StyledRowLink = styled(Link)({
  display: 'block',
  width: '100%',
  minHeight: '100%',
  textDecoration: 'none',
  color: 'inherit',
  boxSizing: 'border-box',
});

export const StyledTable = styled(Table)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.grey[100],
  tableLayout: 'fixed',
  borderCollapse: 'separate',
  borderSpacing: 0,
  '& tbody': {
    display: 'block',
    flex: 1,
    overflowY: 'auto', // Only vertical scrolling for body
    overflowX: 'hidden', // Prevent horizontal scrollbar on tbody
    scrollbarGutter: 'stable both-edges',
    width: '100%',
    minHeight: 0,
  },
  '& thead': {
    display: 'block',
    width: '100%',
    flexShrink: 0,
    overflowY: 'hidden', // No scrolling on header
    overflowX: 'hidden', // Prevent horizontal scrollbar on thead
    scrollbarGutter: 'stable both-edges',
  },
  '& thead tr, & tbody tr': {
    display: 'table',
    width: '100%',
    tableLayout: 'fixed',
  },
}));

/**
 * Column metadata for styling and behavior
 */
export interface ColumnMeta {
  /** Whether the column content should be styled as a primary link */
  isPrimaryColor?: boolean;
}

/**
 * Column meta for sortable columns. Put sortBy in columnDef.meta when enableSorting is true.
 */
export interface SortableColumnMeta {
  sortBy?: string;
}

/**
 * Props for the reusable DataTable component
 */
export interface DataTableProps<TData, TValue> {
  /** Array of data items to display */
  data: TData[];

  /** TanStack Table column definitions (header text, enableSorting, meta.sortBy for sort) */
  columns: ColumnDef<TData, TValue>[];

  /**
   * Called when user clicks sort on a column. Column must have enableSorting and meta.sortBy.
   */
  onSort?: (sortBy: string) => void;

  /**
   * Optional function to make each row a link (enables middle-click / open in new tab).
   * When provided, rows render as links.
   */
  getRowLink?: (
    item: TData,
    rowIndex: number,
  ) => { pathname: string; state?: unknown } | undefined;

  /** Pagination configuration */
  paginationProps?: {
    pageSize: number;
    currentPage: number;
    itemCount: number;
    onPageSelected: (pageIndex: number) => void;
  };

  /** Accessibility label for the table */
  ariaLabel?: string;

  /** Minimum table width (default: 650) */
  minTableWidth?: number;

  /** Additional table styling */
  tableStyle?: React.CSSProperties;
}

/**
 * Reusable data table component with built-in TanStack Table integration
 *
 * This component automatically handles:
 * - TanStack Table width calculations and responsive behavior
 * - Consistent table structure and styling
 * - Column width synchronization
 * - Pagination rendering
 */
export const DataTable = <TData, TValue>({
  data,
  columns,
  onSort,
  getRowLink,
  paginationProps,
  ariaLabel = 'data table',
  minTableWidth = 650,
  tableStyle,
}: DataTableProps<TData, TValue>) => {
  const isRowClickable = !!getRowLink;
  // Create TanStack table instance for width calculations
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    enableSorting: false, // Server-side sort via onSort
  });

  // Per-column styles and total table width.
  const { columnStyles, totalTableWidth } = useMemo(() => {
    const stylesMap = new Map<string, React.CSSProperties>();
    let totalWidth = 0;

    table.getHeaderGroups()[0]?.headers.forEach((header) => {
      const columnDef = columns.find((column) => column.id === header.id)!;
      const width = header.getSize();
      totalWidth += width;

      const hasExplicitMax =
        columnDef.maxSize != null &&
        columnDef.maxSize < Number.MAX_SAFE_INTEGER;

      stylesMap.set(
        header.id,
        hasExplicitMax
          ? {
              width: `${width}px`,
              minWidth: `${width}px`,
              maxWidth: `${columnDef.maxSize}px`,
              flex: 'none',
            }
          : {
              minWidth: `${width}px`,
              width: 'auto',
            },
      );
    });

    return {
      columnStyles: stylesMap,
      totalTableWidth: Math.max(totalWidth, minTableWidth),
    };
  }, [table, columns, minTableWidth]);

  return (
    <ListContainer>
      <TableContainerWrapper>
        <TableWrapper>
          <StyledTable
            aria-label={ariaLabel}
            style={{
              minWidth: totalTableWidth,
              width: '100%',
              ...tableStyle,
            }}
          >
            <TableHead>
              <TableRow>
                {table.getHeaderGroups()[0]?.headers.map((tanstackHeader) => {
                  const columnDef = tanstackHeader.column.columnDef;
                  const headerContent =
                    typeof columnDef.header === 'string'
                      ? columnDef.header
                      : typeof columnDef.header === 'function'
                        ? columnDef.header({
                            column: tanstackHeader.column,
                            header: tanstackHeader,
                            table,
                          })
                        : '';
                  const meta = columnDef.meta as SortableColumnMeta | undefined;
                  const sortBy = meta?.sortBy;
                  const isSortable =
                    columnDef.enableSorting && sortBy != null && !!onSort;
                  return (
                    <StyledHeaderCell
                      key={tanstackHeader.id}
                      align='left'
                      style={columnStyles.get(tanstackHeader.id)}
                    >
                      {headerContent}
                      {isSortable && (
                        <button
                          type='button'
                          onClick={() => onSort!(sortBy!)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            alignItems: 'center',
                          }}
                          title={`Sort by ${headerContent}`}
                        >
                          <img
                            src={sort}
                            alt='Sort'
                            style={{
                              width: '7.2px',
                              height: '10px',
                              marginLeft: '6px',
                            }}
                          />
                        </button>
                      )}
                    </StyledHeaderCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {table.getRowModel().rows.map((row, rowIndex) => {
                const rowLink = getRowLink?.(row.original, rowIndex);

                return (
                  <TableRow
                    key={row.id}
                    hover={isRowClickable}
                    sx={isRowClickable ? { cursor: 'pointer' } : undefined}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const cellFunction = cell.column.columnDef.cell as (
                        context: unknown,
                      ) => React.ReactNode;
                      const cellValue = cellFunction(cell.getContext());

                      const meta = cell.column.columnDef.meta as
                        | ColumnMeta
                        | undefined;
                      const isPrimaryColor = meta?.isPrimaryColor ?? false;

                      const inner = (
                        <BodyCellInner $isPrimary={isPrimaryColor}>
                          {cellValue}
                        </BodyCellInner>
                      );

                      return (
                        <StyledBodyCell
                          key={cell.id}
                          align='left'
                          style={columnStyles.get(cell.column.id)}
                        >
                          {rowLink ? (
                            <StyledRowLink to={rowLink} state={rowLink.state}>
                              {inner}
                            </StyledRowLink>
                          ) : (
                            inner
                          )}
                        </StyledBodyCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </StyledTable>
        </TableWrapper>
      </TableContainerWrapper>

      {paginationProps && (
        <PaginationContainer>
          <Pagination
            data={{
              pageSize: paginationProps.pageSize,
              currentPage: paginationProps.currentPage,
              itemCount: paginationProps.itemCount,
            }}
            onPageSelected={paginationProps.onPageSelected}
          />
        </PaginationContainer>
      )}
    </ListContainer>
  );
};
